/**
 * Página de relatórios financeiros com visualizações simples
 * 
 * Esta página apresenta visualizações dos dados financeiros,
 * incluindo gastos por categoria, evolução do patrimônio, receitas vs despesas
 * e divisão de gastos. Utiliza dados pré-calculados do backend via
 * saldoMensalCacheService para otimizar leituras do Firestore.
 * 
 * Estratégia: cache-first com invalidação por CRUD.
 * Economia: 6 leituras Firestore (documentos pré-calculados) vs 6×N leituras (transações brutas)
 * 
 * @author Amazon Q Developer
 * @since 1.0.0
 */

import { LitElement, html } from 'lit'
import '../components/premium-guard.js'
import { contaCacheService } from '../core/services/conta-cache.service.js'
import { saldoMensalCacheService } from '../core/services/saldo-mensal-cache.service.js'
import { ProtectedPageMixin } from '../core/mixins/protected-page.mixin.js'

/**
 * Componente principal da página de relatórios
 * 
 * Gerencia carregamento de dados pré-calculados e apresentação visual
 * de informações financeiras com gráficos simples.
 * Usa saldoMensalCacheService.obterHistorico(6) para obter dados de evolução.
 */
export class Relatorios extends ProtectedPageMixin(LitElement) {
  static properties = {
    selectedPeriod: { type: String },
    contas: { type: Array },
    loading: { type: Boolean },
    dadosRelatorio: { type: Object },
    semDados: { type: Boolean },
    authError: { type: String }
  }

  /**
   * Mantém os estilos do Bulma CSS framework
   * 
   * @returns {HTMLElement} O próprio elemento para manter estilos globais
   */
  createRenderRoot() {
    return this // manter estilos do Bulma
  }

  /**
   * Inicializa o componente da página de relatórios
   * 
   * Define período atual, carrega dados iniciais e configura
   * serviços de cache para dados pré-calculados.
   */
  constructor() {
    super()
    this.selectedPeriod = this._getCurrentPeriod()
    this.contas = []
    this.loading = true
    this.dadosRelatorio = {}
    this.semDados = false
    /** @type {string|null} Mensagem de erro de autenticação (timeout ou falha) */
    this.authError = null
    
    // Serviços de cache inteligente
    this.saldoMensalService = saldoMensalCacheService
    this.contaCacheService = contaCacheService

    // Bind dos handlers para remoção correta no disconnectedCallback
    this._boundHandleSaldoInvalidado = this._handleSaldoInvalidado.bind(this)
  }

  /**
   * Registra listeners de eventos e carrega dados ao conectar ao DOM.
   * Usa ProtectedPageMixin para aguardar autenticação antes de carregar dados.
   * Escuta evento de invalidação de saldo mensal para recarregar dados afetados.
   */
  async connectedCallback() {
    super.connectedCallback()

    // Escutar evento de invalidação de saldo mensal para recarregar dados
    window.addEventListener('saldo-mensal-cache-invalidado', this._boundHandleSaldoInvalidado)

    this._loadWithAuthGuard(async () => {
      await this._loadData()
    })
  }

  /**
   * Remove listeners de eventos ao desconectar o componente do DOM
   * Previne memory leaks e chamadas a componentes destruídos
   */
  disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('saldo-mensal-cache-invalidado', this._boundHandleSaldoInvalidado)
  }

  /**
   * Carrega dados necessários para os relatórios usando dados pré-calculados
   * 
   * Busca histórico de saldos mensais (6 meses) e contas em paralelo.
   * Usa saldoMensalCacheService ao invés de calcular localmente a partir
   * de transações brutas, economizando leituras no Firestore.
   * 
   * @private
   */
  async _loadData() {
    this.loading = true
    this.semDados = false

    try {
      const [historico, contas] = await Promise.all([
        this.saldoMensalService.obterHistorico(6),
        this.contaCacheService.listar()
      ])
      
      this.contas = contas
      this._processarHistorico(historico)
    } catch (error) {
      console.error('Erro ao carregar dados dos relatórios:', error)
      this.semDados = true
    } finally {
      this.loading = false
    }
  }

  /**
   * Processa histórico de saldos mensais pré-calculados para gráficos
   * 
   * Extrai dados relevantes de cada mês para alimentar os gráficos:
   * - receitasPorCategoria/despesasPorCategoria → gráfico de distribuição
   * - totalReceitas/totalDespesas por mês → gráfico de evolução
   * - patrimonioTotal por mês → gráfico de patrimônio
   * - gastoPessoal/gastoCompartilhado → gráfico de divisão de gastos
   * 
   * @param {Array} historico - Lista de saldos mensais do backend
   * @private
   */
  _processarHistorico(historico) {
    // Trata cenário sem dados (meses sem cálculo)
    if (!historico || historico.length === 0) {
      this.semDados = true
      this.dadosRelatorio = {
        categorias: [],
        patrimonio: 0,
        receitaDespesa: { receitas: 0, despesas: 0 },
        divisao: { compartilhado: 0, pessoal: 0 },
        evolucao: []
      }
      return
    }

    // Usa dados do mês mais recente para gráficos de distribuição
    const mesAtual = historico[0]

    // Gráfico de distribuição por categoria (despesas)
    const categorias = this._extrairCategoriasDespesas(mesAtual)

    // Gráfico de patrimônio: usa patrimonioTotal do mês mais recente
    const patrimonio = mesAtual.patrimonioTotal || 0

    // Gráfico de receitas vs despesas: totalReceitas/totalDespesas do mês atual
    const receitaDespesa = {
      receitas: mesAtual.totalReceitas || 0,
      despesas: mesAtual.totalDespesas || 0
    }

    // Gráfico de divisão de gastos: gastoPessoal/gastoCompartilhado
    const divisao = {
      compartilhado: mesAtual.gastoCompartilhado || 0,
      pessoal: mesAtual.gastoPessoal || 0
    }

    // Dados de evolução mensal (todos os meses do histórico)
    const evolucao = historico.map(saldo => ({
      mes: saldo.mes,
      ano: saldo.ano,
      totalReceitas: saldo.totalReceitas || 0,
      totalDespesas: saldo.totalDespesas || 0,
      patrimonioTotal: saldo.patrimonioTotal || 0
    }))

    this.dadosRelatorio = {
      categorias,
      patrimonio,
      receitaDespesa,
      divisao,
      evolucao
    }
  }

  /**
   * Extrai categorias de despesas do saldo mensal pré-calculado
   * Transforma o mapa despesasPorCategoria em array ordenado por total.
   * 
   * @param {Object} saldoMensal - Saldo mensal com despesasPorCategoria
   * @returns {Array} Top 5 categorias de despesas ordenadas por total
   * @private
   */
  _extrairCategoriasDespesas(saldoMensal) {
    const despesasPorCategoria = saldoMensal.despesasPorCategoria || {}

    return Object.entries(despesasPorCategoria)
      .map(([id, dados]) => ({
        nome: dados.categoriaNome || 'Sem categoria',
        emoji: dados.emoji || '💰',
        total: dados.total || 0
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 5) // Top 5 categorias
  }

  /**
   * Manipula evento de invalidação do cache de saldo mensal
   * Recarrega dados quando um período visível é invalidado.
   * Garante que relatórios reflitam dados atualizados após CRUD de transações.
   *
   * @param {CustomEvent} event - Evento 'saldo-mensal-cache-invalidado' com detail { mes, ano }
   * @private
   */
  async _handleSaldoInvalidado(event) {
    const { mes, ano } = event.detail

    // Verifica se o período invalidado está dentro do histórico visível (6 meses)
    const periodosVisiveis = this._calcularPeriodosVisiveis()
    const periodoInvalidado = periodosVisiveis.find(p => p.mes === mes && p.ano === ano)

    if (periodoInvalidado) {
      await this._loadData()
      this.requestUpdate()
    }
  }

  /**
   * Calcula os períodos visíveis no relatório (últimos 6 meses)
   * Usado para verificar se um período invalidado afeta a visualização atual.
   * 
   * @returns {Array<{mes: string, ano: string}>} Lista de períodos visíveis
   * @private
   */
  _calcularPeriodosVisiveis() {
    const periodos = []
    const hoje = new Date()

    for (let i = 0; i < 6; i++) {
      const data = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1)
      periodos.push({
        mes: String(data.getMonth() + 1).padStart(2, '0'),
        ano: String(data.getFullYear())
      })
    }

    return periodos
  }

  /**
   * Formata valor numérico para moeda brasileira (BRL)
   * @param {number} amount - Valor a ser formatado
   * @returns {string} Valor formatado (ex: 'R$ 1.500,00')
   */
  _formatCurrency(amount) {
    return (amount || 0).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })
  }

  /**
   * Manipula mudança de período no filtro
   * Busca saldo mensal específico quando o usuário muda o período selecionado.
   * 
   * @param {Event} e - Evento de change do select
   */
  async _onPeriodChange(e) {
    this.selectedPeriod = e.target.value

    // Busca dados do período selecionado
    const meses = this._getMesesFromPeriod(this.selectedPeriod)
    this.loading = true

    try {
      const historico = await this.saldoMensalService.obterHistorico(meses)
      this._processarHistorico(historico)
    } catch (error) {
      console.error('Erro ao carregar dados do período:', error)
      this.semDados = true
    } finally {
      this.loading = false
    }
  }

  /**
   * Converte período selecionado em quantidade de meses para buscar
   * Mapeia o texto do filtro para o parâmetro numérico do obterHistorico.
   * 
   * @param {string} period - Texto do período selecionado
   * @returns {number} Quantidade de meses para buscar
   * @private
   */
  _getMesesFromPeriod(period) {
    if (period.includes('3 meses')) return 3
    if (period.includes('12 meses') || period.includes('1 ano')) return 12
    return 6 // padrão: 6 meses
  }

  /**
   * Retorna o período atual formatado para exibição
   * @returns {string} Período atual (ex: 'Últimos 6 meses')
   * @private
   */
  _getCurrentPeriod() {
    return 'Últimos 6 meses'
  }

  /**
   * Retorna lista de períodos disponíveis para filtro
   * @returns {Array<string>} Opções de período para o select
   * @private
   */
  _getPeriodosDisponiveis() {
    return ['Últimos 3 meses', 'Últimos 6 meses', 'Últimos 12 meses']
  }

  render() {
    return html`
      <premium-guard>
        <section class="section">
        <div class="container" style="padding-bottom: 90px;">
          <div class="is-flex is-justify-content-space-between is-align-items-center mb-5">
            <h1 class="title is-4" data-cy="relatorios-title">Relatórios</h1>
            <div class="field" style="width: 250px;">
              <div class="control">
                <div class="select is-fullwidth">
                  <select 
                    data-cy="filter-periodo"
                    .value="${this.selectedPeriod}"
                    @change="${this._onPeriodChange}"
                  >
                    ${this._getPeriodosDisponiveis().map(periodo => html`
                      <option value="${periodo}" ?selected="${periodo === this.selectedPeriod}">${periodo}</option>
                    `)}
                  </select>
                </div>
              </div>
            </div>
          </div>

          ${this.semDados && !this.loading ? this._renderSemDados() : ''}
          ${this.authError ? html`
            <div class="notification is-danger is-light" data-cy="auth-error-message">
              <span class="icon">
                <i class="fas fa-exclamation-triangle"></i>
              </span>
              <span>${this.authError}</span>
            </div>
          ` : ''}

          <div class="columns is-multiline">
            <!-- Gastos por Categoria -->
            <div class="column is-6">
              <div class="card" data-cy="card-gastos-categoria">
                <header class="card-header">
                  <p class="card-header-title">Gastos por Categoria</p>
                </header>
                <div class="card-content">
                  ${this._renderGastosCategoriaContent()}
                </div>
              </div>
            </div>

            <!-- Divisão de Gastos -->
            <div class="column is-6">
              <div class="card" data-cy="card-divisao-gastos">
                <header class="card-header">
                  <p class="card-header-title">Divisão de Gastos</p>
                </header>
                <div class="card-content">
                  <div class="content">
                    ${this._renderDivisaoGastos()}
                  </div>
                </div>
              </div>
            </div>

            <!-- Resumo Financeiro -->
            <div class="column is-6">
              <div class="card" data-cy="card-resumo-financeiro">
                <header class="card-header">
                  <p class="card-header-title">Resumo Financeiro</p>
                </header>
                <div class="card-content">
                  ${this._renderResumoFinanceiro()}
                </div>
              </div>
            </div>

            <!-- Patrimônio Total -->
            <div class="column is-6">
              <div class="card" data-cy="card-patrimonio">
                <header class="card-header">
                  <p class="card-header-title">Patrimônio Total</p>
                </header>
                <div class="card-content">
                  ${this._renderPatrimonio()}
                </div>
              </div>
            </div>
          </div>
        </div>
        </section>
      </premium-guard>
    `
  }

  /**
   * Renderiza mensagem quando não há dados disponíveis
   * Exibida quando o histórico está vazio (meses sem cálculo)
   * @returns {import('lit').TemplateResult} Template HTML da mensagem sem dados
   * @private
   */
  _renderSemDados() {
    return html`
      <div class="notification is-info is-light" data-cy="relatorios-sem-dados">
        <span class="icon">
          <i class="fas fa-info-circle"></i>
        </span>
        <span>Nenhum dado disponível para o período selecionado. Os relatórios serão exibidos após o registro de transações.</span>
      </div>
    `
  }

  _renderGastosCategoriaContent() {
    if (this.loading) {
      return html`
        <div class="has-text-centered py-6" data-cy="loading-categorias">
          <span class="icon is-large has-text-grey-light">
            <i class="fas fa-spinner fa-pulse fa-3x"></i>
          </span>
          <p class="has-text-grey">Carregando dados...</p>
        </div>
      `
    }

    const categorias = this.dadosRelatorio.categorias || []
    if (categorias.length === 0) {
      return html`
        <div class="has-text-centered py-6" data-cy="empty-categorias">
          <span class="icon is-large has-text-grey-light">
            <i class="fas fa-chart-pie fa-3x"></i>
          </span>
          <p class="has-text-grey">Nenhum gasto por categoria registrado</p>
          <p class="has-text-grey-light is-size-7">Adicione transações para visualizar os dados</p>
        </div>
      `
    }

    const totalGastos = categorias.reduce((sum, cat) => sum + cat.total, 0)

    return html`
      <div class="content" data-cy="lista-categorias">
        ${categorias.map(categoria => {
          const percentual = totalGastos > 0 ? (categoria.total / totalGastos * 100) : 0
          return html`
            <div class="mb-4">
              <div class="is-flex is-justify-content-space-between is-align-items-center mb-2">
                <div class="is-flex is-align-items-center" style="gap: 0.5rem;">
                  <span>${categoria.emoji}</span>
                  <span>${categoria.nome}</span>
                </div>
                <div class="has-text-right">
                  <div class="has-text-weight-medium">${this._formatCurrency(categoria.total)}</div>
                  <div class="is-size-7 has-text-grey">${percentual.toFixed(1)}%</div>
                </div>
              </div>
              <div class="progress-container" style="height: 8px; background-color: var(--bg-surface-secondary, #1D2740); border-radius: 4px; overflow: hidden;">
                <div 
                  class="progress-bar has-background-primary"
                  style="height: 100%; width: ${percentual}%; transition: width 0.3s ease; border-radius: 4px;"
                ></div>
              </div>
            </div>
          `
        })}
      </div>
    `
  }

  _renderDivisaoGastos() {
    if (this.loading) {
      return html`
        <div class="has-text-centered py-6" data-cy="loading-divisao">
          <span class="icon is-large has-text-grey-light">
            <i class="fas fa-spinner fa-pulse fa-3x"></i>
          </span>
          <p class="has-text-grey">Carregando dados...</p>
        </div>
      `
    }

    const divisao = this.dadosRelatorio.divisao || { compartilhado: 0, pessoal: 0 }
    const total = divisao.compartilhado + divisao.pessoal
    
    if (total === 0) {
      return html`
        <div class="has-text-centered py-6" data-cy="empty-divisao">
          <span class="icon is-large has-text-grey-light">
            <i class="fas fa-chart-pie fa-3x"></i>
          </span>
          <p class="has-text-grey">Nenhum gasto registrado</p>
        </div>
      `
    }
    
    const percentCompartilhado = ((divisao.compartilhado / total) * 100).toFixed(1)
    const percentPessoal = ((divisao.pessoal / total) * 100).toFixed(1)
    
    return html`
      <div class="mb-4">
        <div class="is-flex is-justify-content-space-between mb-2">
          <span>👥 Compartilhado</span>
          <div class="has-text-right">
            <div class="has-text-weight-medium" data-cy="valor-compartilhado">${this._formatCurrency(divisao.compartilhado)}</div>
            <div class="is-size-7 has-text-grey">${percentCompartilhado}%</div>
          </div>
        </div>
        <div class="progress-container mb-3" style="height: 8px; background-color: var(--bg-surface-secondary, #1D2740); border-radius: 4px; overflow: hidden;">
          <div 
            class="progress-bar has-background-info"
            style="height: 100%; width: ${percentCompartilhado}%; transition: width 0.3s ease; border-radius: 4px;"
            data-cy="progress-compartilhado"
          ></div>
        </div>
      </div>
      
      <div class="mb-4">
        <div class="is-flex is-justify-content-space-between mb-2">
          <span>👤 Pessoal</span>
          <div class="has-text-right">
            <div class="has-text-weight-medium" data-cy="valor-pessoal">${this._formatCurrency(divisao.pessoal)}</div>
            <div class="is-size-7 has-text-grey">${percentPessoal}%</div>
          </div>
        </div>
        <div class="progress-container mb-3" style="height: 8px; background-color: var(--bg-surface-secondary, #1D2740); border-radius: 4px; overflow: hidden;">
          <div 
            class="progress-bar has-background-success"
            style="height: 100%; width: ${percentPessoal}%; transition: width 0.3s ease; border-radius: 4px;"
            data-cy="progress-pessoal"
          ></div>
        </div>
      </div>
    `
  }

  _renderResumoFinanceiro() {
    if (this.loading) {
      return html`
        <div class="has-text-centered py-6" data-cy="loading-resumo">
          <span class="icon is-large has-text-grey-light">
            <i class="fas fa-spinner fa-pulse fa-3x"></i>
          </span>
          <p class="has-text-grey">Carregando dados...</p>
        </div>
      `
    }

    const dados = this.dadosRelatorio.receitaDespesa || { receitas: 0, despesas: 0 }
    const saldo = dados.receitas - dados.despesas

    return html`
      <div class="content" data-cy="resumo-financeiro-content">
        <div class="is-flex is-justify-content-space-between is-align-items-center mb-3 p-3 has-background-success-light" style="border-radius: 6px;">
          <div class="is-flex is-align-items-center" style="gap: 0.5rem;">
            <span class="icon has-text-success">
              <i class="fas fa-arrow-up"></i>
            </span>
            <span>Receitas</span>
          </div>
          <span class="has-text-weight-medium has-text-success" data-cy="valor-receitas">${this._formatCurrency(dados.receitas)}</span>
        </div>

        <div class="is-flex is-justify-content-space-between is-align-items-center mb-3 p-3 has-background-danger-light" style="border-radius: 6px;">
          <div class="is-flex is-align-items-center" style="gap: 0.5rem;">
            <span class="icon has-text-danger">
              <i class="fas fa-arrow-down"></i>
            </span>
            <span>Despesas</span>
          </div>
          <span class="has-text-weight-medium has-text-danger" data-cy="valor-despesas">${this._formatCurrency(dados.despesas)}</span>
        </div>

        <div class="is-flex is-justify-content-space-between is-align-items-center p-3 has-background-light" style="border-radius: 6px; border: 2px solid var(--border, #26304A);">
          <div class="is-flex is-align-items-center" style="gap: 0.5rem;">
            <span class="icon ${saldo >= 0 ? 'has-text-success' : 'has-text-danger'}">
              <i class="fas fa-equals"></i>
            </span>
            <span class="has-text-weight-medium">Saldo</span>
          </div>
          <span class="has-text-weight-bold ${saldo >= 0 ? 'has-text-success' : 'has-text-danger'}" data-cy="valor-saldo">${this._formatCurrency(saldo)}</span>
        </div>
      </div>
    `
  }

  _renderPatrimonio() {
    if (this.loading) {
      return html`
        <div class="has-text-centered py-6" data-cy="loading-patrimonio">
          <span class="icon is-large has-text-grey-light">
            <i class="fas fa-spinner fa-pulse fa-3x"></i>
          </span>
          <p class="has-text-grey">Carregando dados...</p>
        </div>
      `
    }

    const patrimonio = this.dadosRelatorio.patrimonio || 0

    return html`
      <div class="content has-text-centered" data-cy="patrimonio-content">
        <div class="mb-4">
          <span class="icon is-large has-text-primary">
            <i class="fas fa-piggy-bank fa-3x"></i>
          </span>
        </div>
        <div class="is-size-3 has-text-weight-bold has-text-primary mb-2" data-cy="valor-patrimonio">
          ${this._formatCurrency(patrimonio)}
        </div>
        <p class="has-text-grey">Patrimônio total acumulado</p>
        
        ${this.contas.length > 0 ? html`
          <div class="mt-4" data-cy="distribuicao-contas">
            <p class="has-text-weight-medium mb-2">Distribuição por conta:</p>
            ${this.contas.map(conta => html`
              <div class="is-flex is-justify-content-space-between is-align-items-center mb-2 p-2 has-background-light" style="border-radius: 4px;">
                <span class="is-size-7">${conta.nome}</span>
                <span class="is-size-7 has-text-weight-medium">${this._formatCurrency(conta.saldoAtual || 0)}</span>
              </div>
            `)}
          </div>
        ` : ''}
      </div>
    `
  }
}

customElements.define('relatorios-page', Relatorios)
